class MonclerVerticalSlider extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.viewport = this.querySelector('.moncler-vertical-slider__viewport');
    this.track = this.querySelector('.moncler-vertical-slider__track');
    this.slides = Array.from(this.querySelectorAll('.moncler-vertical-slider__slide'));
    this.dots = Array.from(this.querySelectorAll('.moncler-vertical-slider__dot'));

    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this.current = 0;
    this.isAnimating = false;
    this.total = this.slides.length;
    this.autoplayMs = this.resolveAutoplayMs();
    this.transitionMs = parseInt(this.dataset.transition, 10) || 700;
    this.autoplayTimer = null;
    this._animUnlockTimer = null;

    this.applyTransition();
    this.bindEvents();
    this.updatePosition(false);

    if (this.total > 1) {
      requestAnimationFrame(() => {
        this.updatePosition(false);
        this.startAutoplay();
      });
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.clearAnimationLock();
    this.unbindEvents();
  }

  resolveAutoplayMs() {
    const fromDataset = parseInt(this.dataset.autoplay, 10);
    if (!Number.isNaN(fromDataset) && fromDataset > 0) return fromDataset;
    const fromSeconds = parseFloat(this.dataset.autoplaySeconds);
    if (!Number.isNaN(fromSeconds) && fromSeconds > 0) return Math.round(fromSeconds * 1000);
    return 7000;
  }

  applyTransition() {
    this.track.style.transition = `transform ${this.transitionMs}ms cubic-bezier(0.4, 0, 0.2, 1)`;
  }

  bindEvents() {
    this._onTransitionEnd = this.onTransitionEnd.bind(this);
    this._onResize = () => this.updatePosition(false);

    // No wheel / touch / keyboard slide changes — page scrolls normally.
    // Dots remain clickable; autoplay stays active.
    this.track.addEventListener('transitionend', this._onTransitionEnd);
    window.addEventListener('resize', this._onResize);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.resetAutoplay();
      });
    });

    if (this.dataset.pauseOnHover === 'true') {
      this._onMouseEnter = () => this.stopAutoplay();
      this._onMouseLeave = () => this.startAutoplay();
      this.viewport.addEventListener('mouseenter', this._onMouseEnter);
      this.viewport.addEventListener('mouseleave', this._onMouseLeave);
    }
  }

  unbindEvents() {
    if (!this.track) return;
    this.track.removeEventListener('transitionend', this._onTransitionEnd);
    window.removeEventListener('resize', this._onResize);
    if (this.viewport && this._onMouseEnter) {
      this.viewport.removeEventListener('mouseenter', this._onMouseEnter);
      this.viewport.removeEventListener('mouseleave', this._onMouseLeave);
    }
  }

  getSlideHeight() {
    const measured = this.viewport ? this.viewport.offsetHeight : 0;
    return measured > 0 ? measured : window.innerHeight;
  }

  lockAnimation() {
    this.isAnimating = true;
    this.clearAnimationLock();
    this._animUnlockTimer = window.setTimeout(() => {
      this.isAnimating = false;
    }, this.transitionMs + 200);
  }

  clearAnimationLock() {
    if (this._animUnlockTimer) {
      window.clearTimeout(this._animUnlockTimer);
      this._animUnlockTimer = null;
    }
  }

  unlockAnimation() {
    this.clearAnimationLock();
    this.isAnimating = false;
  }

  updatePosition(animate = true) {
    // Keep index in range — reliable 0 ↔ 1 ↔ 0 loop (no clone jump bugs)
    if (this.current < 0) this.current = this.total - 1;
    if (this.current >= this.total) this.current = 0;

    const offset = this.current * this.getSlideHeight();
    if (!animate) {
      this.track.style.transition = 'none';
    } else {
      this.applyTransition();
    }
    this.track.style.transform = `translate3d(0, -${offset}px, 0)`;
    this.updateDots();
    if (!animate) {
      requestAnimationFrame(() => this.applyTransition());
    }
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.current;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  onTransitionEnd(event) {
    if (event.target !== this.track) return;
    if (event.propertyName !== 'transform') return;
    this.unlockAnimation();
  }

  advanceAutoplay() {
    if (this.total < 2) return;
    if (this.isAnimating) return;

    this.lockAnimation();
    this.current = (this.current + 1) % this.total;
    this.updatePosition(true);
  }

  next(fromAutoplay = false) {
    if (this.total < 2) return;
    if (this.isAnimating) return;

    this.lockAnimation();
    this.current = (this.current + 1) % this.total;
    this.updatePosition(true);

    if (!fromAutoplay) this.resetAutoplay();
  }

  prev() {
    if (this.total < 2 || this.isAnimating) return;

    this.lockAnimation();
    this.current = (this.current - 1 + this.total) % this.total;
    this.updatePosition(true);
    this.resetAutoplay();
  }

  goTo(index) {
    if (this.total < 2 || this.isAnimating) return;
    if (index < 0 || index >= this.total || index === this.current) return;

    this.lockAnimation();
    this.current = index;
    this.updatePosition(true);
  }

  // Scroll / wheel / touch / keyboard must not change slides.
  onWheel() {}
  onTouchStart() {}
  onTouchEnd() {}
  onKeyDown() {}

  startAutoplay() {
    this.stopAutoplay();
    if (this.total < 2) return;

    this.autoplayTimer = window.setInterval(() => {
      this.advanceAutoplay();
    }, this.autoplayMs);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('moncler-vertical-slider')) {
  customElements.define('moncler-vertical-slider', MonclerVerticalSlider);
}

function initMonclerStickyNav() {
  document.querySelectorAll('[data-moncler-sticky-nav]').forEach((header) => {
    if (header._monclerStickyBound) return;
    header._monclerStickyBound = true;

    const hero = header.closest('.moncler-vertical-hero');
    const announcement = hero
      ? hero.querySelector('.moncler-vertical-hero__announcement')
      : document.querySelector('.moncler-vertical-hero__announcement')
        || document.querySelector('.tooj-site-header .moncler-vertical-hero__announcement');

    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateNav = () => {
      ticking = false;
      const currentY = window.scrollY;
      const threshold = announcement ? announcement.offsetHeight : 36;
      const scrollingDown = currentY > lastScrollY;
      const scrollingUp = currentY < lastScrollY;

      // After announcement scrolls away, nav becomes sticky (original behavior)
      const makeSticky = currentY > threshold;
      header.classList.toggle('is-sticky', makeSticky);

      if (!makeSticky) {
        header.classList.remove('is-nav-hidden');
      } else if (scrollingDown && currentY > threshold + 40) {
        header.classList.add('is-nav-hidden');
      } else if (scrollingUp) {
        header.classList.remove('is-nav-hidden');
      }

      lastScrollY = currentY;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateNav);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateNav, { passive: true });
    updateNav();
  });
}

function initToojNavDropdowns() {
  const desktopMq = window.matchMedia('(min-width: 990px) and (hover: hover)');

  const closeAll = (except) => {
    document.querySelectorAll('.moncler-vertical-hero__nav-item.has-dropdown.is-open').forEach((item) => {
      if (except && item === except) return;
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-tooj-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  };

  const openItem = (item) => {
    if (!item) return;
    closeAll(item);
    item.classList.add('is-open');
    const trigger = item.querySelector('[data-tooj-dropdown-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  };

  const closeItem = (item) => {
    if (!item) return;
    item.classList.remove('is-open');
    const trigger = item.querySelector('[data-tooj-dropdown-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };

  document.querySelectorAll('.moncler-vertical-hero__nav-item.has-dropdown').forEach((item) => {
    const trigger = item.querySelector('[data-tooj-dropdown-trigger]');
    if (!trigger || trigger._toojDropdownBound) return;
    trigger._toojDropdownBound = true;

    // Desktop: open on hover
    item.addEventListener('mouseenter', () => {
      if (!desktopMq.matches) return;
      openItem(item);
    });
    item.addEventListener('mouseleave', () => {
      if (!desktopMq.matches) return;
      closeItem(item);
    });

    // Keyboard / focus
    item.addEventListener('focusin', () => {
      if (!desktopMq.matches) return;
      openItem(item);
    });
    item.addEventListener('focusout', (event) => {
      if (!desktopMq.matches) return;
      if (!item.contains(event.relatedTarget)) closeItem(item);
    });

    // Mobile / touch: click toggle (desktop click just follows hover state)
    trigger.addEventListener('click', (event) => {
      if (desktopMq.matches) {
        // Keep parent from navigating away; hover already opens the menu
        event.preventDefault();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const willOpen = !item.classList.contains('is-open');
      if (willOpen) openItem(item);
      else closeItem(item);
    });
  });

  if (!document.documentElement._toojDropdownDocBound) {
    document.documentElement._toojDropdownDocBound = true;

    document.addEventListener('click', (event) => {
      if (desktopMq.matches) return;
      if (!event.target.closest('.moncler-vertical-hero__nav-item.has-dropdown')) {
        closeAll();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll();
    });
  }
}

function initToojMobileNav() {
  const headerRoot = document.querySelector('.tooj-site-header');
  if (!headerRoot || headerRoot._toojMobileBound) return;
  headerRoot._toojMobileBound = true;

  const toggle = headerRoot.querySelector('[data-tooj-menu-toggle]');
  const closeBtn = headerRoot.querySelector('[data-tooj-menu-close]');
  const backdrop = headerRoot.querySelector('[data-tooj-menu-backdrop]');
  const nav = headerRoot.querySelector('[data-tooj-mobile-nav]');
  const stickyHeader = headerRoot.querySelector('[data-moncler-sticky-nav]');
  if (!toggle || !nav) return;

  const mq = window.matchMedia('(max-width: 989px)');
  const navHomeParent = nav.parentElement;
  const navHomeNext = nav.nextElementSibling;

  const placeNav = () => {
    if (mq.matches) {
      // Portal out of sticky header so it can never cover page clicks when closed
      if (nav.parentElement !== document.body) document.body.appendChild(nav);
      if (backdrop && backdrop.parentElement !== document.body) document.body.appendChild(backdrop);
    } else if (navHomeParent && nav.parentElement !== navHomeParent) {
      if (navHomeNext && navHomeNext.parentElement === navHomeParent) {
        navHomeParent.insertBefore(nav, navHomeNext);
      } else {
        navHomeParent.appendChild(nav);
      }
      if (backdrop) headerRoot.appendChild(backdrop);
    }
  };

  const setOpen = (open) => {
    const shouldOpen = !!open && mq.matches;
    document.body.classList.toggle('tooj-nav-open', shouldOpen);
    toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    toggle.setAttribute('aria-label', shouldOpen ? 'Close menu' : 'Open menu');
    if (backdrop) {
      if (shouldOpen) backdrop.removeAttribute('hidden');
      else backdrop.setAttribute('hidden', '');
    }
    if (stickyHeader && shouldOpen) stickyHeader.classList.remove('is-nav-hidden');
  };

  // Always start closed + correctly placed
  document.body.classList.remove('tooj-nav-open');
  placeNav();
  setOpen(false);

  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    placeNav();
    setOpen(!document.body.classList.contains('tooj-nav-open'));
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    });
  }

  if (backdrop) {
    backdrop.addEventListener('click', (event) => {
      event.preventDefault();
      setOpen(false);
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
  });

  nav.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      if (mq.matches) setOpen(false);
    });
  });

  const onBreakpointChange = () => {
    placeNav();
    setOpen(false);
  };

  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onBreakpointChange);
  else if (typeof mq.addListener === 'function') mq.addListener(onBreakpointChange);
}

function initToojAnnouncementSlider() {
  document.querySelectorAll('[data-tooj-announcement-slider]').forEach((root) => {
    if (root.dataset.sliderReady === '1') return;

    const slides = Array.from(root.querySelectorAll('[data-announcement-slide]'));
    if (slides.length < 2) return;

    root.dataset.sliderReady = '1';

    let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (index < 0) index = 0;
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));

    const intervalMs = Math.max(2000, Number(root.dataset.interval) || 4000);
    let timer = null;

    const goTo = (nextIndex) => {
      if (nextIndex === index) return;
      const current = slides[index];
      const nextSlide = slides[nextIndex];

      // Current exits to the right; next enters from the left
      current.classList.add('is-leave-right');
      current.classList.remove('is-active');
      window.setTimeout(() => current.classList.remove('is-leave-right'), 400);

      nextSlide.style.transform = 'translateX(-24px)';
      void nextSlide.offsetWidth;
      nextSlide.style.transform = '';
      nextSlide.classList.add('is-active');
      index = nextIndex;
    };

    const next = () => goTo((index + 1) % slides.length);

    const startAuto = () => {
      stopAuto();
      timer = window.setInterval(next, intervalMs);
    };
    const stopAuto = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);
    startAuto();
  });
}

function initMonclerHero() {
  initMonclerStickyNav();
  initToojNavDropdowns();
  initToojMobileNav();
  initToojAnnouncementSlider();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMonclerHero);
} else {
  initMonclerHero();
}

document.addEventListener('shopify:section:load', initMonclerHero);
